import {Component, DestroyRef, inject} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslocoModule} from '@jsverse/transloco';
import {TermsAcceptanceService, PendingTerm} from './terms-acceptance.service';
import {TermFullTextDialogComponent} from '../signup/term-full-text-dialog.component';

@Component({
  selector: 'app-terms-acceptance',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCard, MatCardContent,
    MatButton,
    MatCheckbox,
    MatProgressSpinnerModule,
    TranslocoModule,
  ],
  templateUrl: './terms-acceptance.component.html',
  styleUrl: './terms-acceptance.component.css',
})
export class TermsAcceptanceComponent {
  pendingTerms: PendingTerm[] = [];
  loading = false;
  submitting = false;
  loadError = false;
  readonly termDecisions = new Map<string, 'accepted' | 'declined'>();

  // OAuth resume params
  private clientId = '';
  private responseType = '';
  private redirectUri = '';
  private scope = '';
  private state = '';
  private codeChallenge = '';
  private codeChallengeMethod = '';
  private audience = '';
  private nonce = '';
  // Story 53.10 AC3 — the flow's id, carried through to the resume.
  private correlationId = '';

  private readonly service = inject(TermsAcceptanceService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe(params => {
      this.clientId = params['client_id'] ?? '';
      this.responseType = params['response_type'] ?? '';
      this.redirectUri = params['redirect_uri'] ?? '';
      this.scope = params['scope'] ?? '';
      this.state = params['state'] ?? '';
      this.codeChallenge = params['code_challenge'] ?? '';
      this.codeChallengeMethod = params['code_challenge_method'] ?? '';
      this.audience = params['audience'] ?? '';
      this.nonce = params['nonce'] ?? '';
      this.correlationId = params['correlation_id'] ?? '';

      if (this.clientId) {
        this.loadPendingTerms();
      }
    });
  }

  get hasUnacceptedMandatoryTerm(): boolean {
    return this.pendingTerms
      .filter(t => t.kind === 'term')
      .some(t => this.termDecisions.get(t.id) !== 'accepted');
  }

  toggleTerm(termId: string, accepted: boolean): void {
    this.termDecisions.set(termId, accepted ? 'accepted' : 'declined');
  }

  isTermAccepted(termId: string): boolean {
    return this.termDecisions.get(termId) === 'accepted';
  }

  openFullText(term: PendingTerm): void {
    this.dialog.open(TermFullTextDialogComponent, {
      data: {title: term.resume, fullText: term.fullText},
      maxWidth: '680px',
      width: '90vw',
    });
  }

  submit(): void {
    if (this.submitting || this.loading || this.loadError) return;
    if (this.hasUnacceptedMandatoryTerm) return;

    this.submitting = true;
    const decisions = this.pendingTerms.map(t => ({
      termId: t.id,
      decision: this.termDecisions.get(t.id) ?? 'declined',
    }));

    this.service.postAcceptance({
      client_id: this.clientId,
      decisions,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: resp => {
        this.submitting = false;
        if (resp.result === 'accepted') {
          this.resumeAuthorize();
        } else {
          this.redirectAccessDenied();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        // The session cookie went away between loading and submitting: the
        // same sign-in round-trip the load performs (53.9 code review).
        if (err.status === 401) {
          this.resumeAuthorize();
          return;
        }
        this.loadError = true;
      },
    });
  }

  private loadPendingTerms(): void {
    this.loading = true;
    this.loadError = false;
    this.service.getPendingTerms(this.clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: terms => {
          this.pendingTerms = terms;
          this.loading = false;
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          // Story 53.9 — no live session (the cookie expired or was cleared):
          // resume /authorize, which sends the user to sign-in with the whole
          // flow intact, as every other page's 401 does.
          if (err.status === 401) {
            this.resumeAuthorize();
            return;
          }
          this.loadError = true;
        },
      });
  }

  private resumeAuthorize(): void {
    window.location.href = this.resumeUrl();
  }

  private resumeUrl(): string {
    // Story 53.9: no session_id — /authorize reads the host-only cookie.
    const params = new URLSearchParams({
      response_type: this.responseType,
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      state: this.state,
      audience: this.audience,
      code_challenge: this.codeChallenge,
      code_challenge_method: this.codeChallengeMethod,
    });
    if (this.nonce) params.set('nonce', this.nonce);
    // Story 53.10 AC3 — resume the flow under its own id, so its events stay
    // one lineage across the interstitial (omitted when there is none).
    if (this.correlationId) params.set('correlation_id', this.correlationId);
    return `/authorize?${params.toString()}`;
  }

  private redirectAccessDenied(): void {
    const params = new URLSearchParams({error: 'access_denied'});
    if (this.state) params.set('state', this.state);
    window.location.href = `${this.redirectUri}?${params.toString()}`;
  }
}
